import * as React from 'react';
import { Link } from 'react-router';
import { __ } from 'react-i18n';
import styled from 'styled-components';
import { rem } from 'polished';
import { InfoText, InfoType } from '../../General/InfoText';
import {
  FlexCol,
  FlexRowCenter,
  FlexColCenterHorizontal,
  FlexRow,
} from '../../../Defaults/Flexbox/Flexbox';
import { Button, ButtonLink } from '../../General/Button';
import { SummaryValue } from './SummaryValue';
import { FinalValue } from './FinalValue';
import { getCartSummaryInfo } from '../../../../utilities/selectors';
import { prop } from '../../../../utilities';
import { Loader } from './ThirdStep';
import { SvgIcon, IconType } from '../../General/SvgIcon';
import { FormInput } from '../../Form/FormInput';

interface Props {
  handleStepChange?: (step: number) => void;
  isFirstStep?: boolean;
  data: any;
  isFetching: boolean;
  addGiftCard: any;
  removeGiftCard: any;
}

function SecondStep({
  handleStepChange,
  data,
  isFirstStep = false,
  isFetching,
  addGiftCard,
  removeGiftCard,
}: Props) {
  const {
    totalPrice,
    totalPriceWithoutVat,
    vatTotal,
    currency,
    totalItemsPriceWithoutVat,
    vatItems,
    totalItemsPrice,
  } = getCartSummaryInfo(data);
  const { giftcards } = data;

  const [currentGiftCard, setCurrentGiftCard] = React.useState(
    giftcards && giftcards.length > 0
      ? prop(giftcards[0], 'card_number', '')
      : '',
  );

  const [showGiftcardItem, setShowGiftcardItem] = React.useState(
    giftcards && giftcards.length > 0,
  );

  const hasActiveGiftCard =
    currentGiftCard && giftcards && giftcards.length > 0;

  const handleGiftCardChange = e => {
    setCurrentGiftCard(e.target.value);
  };

  const handleGiftCard = () => {
    if (hasActiveGiftCard) {
      removeGiftCard(currentGiftCard);
      setCurrentGiftCard('');
      setShowGiftcardItem(false);
    } else {
      if (currentGiftCard.length > 0) {
        setShowGiftcardItem(true);
        addGiftCard(currentGiftCard);
      }
    }
  };

  const backButtonText = isFirstStep ? __('Späť k nakupovaniu') : __('Späť');

  return (
    <>
      <DesktopFields>
        <SummaryValue
          title={__('Cena tovaru bez DPH')}
          value={`${totalItemsPriceWithoutVat} ${currency}`}
        />
        <SummaryValue
          title={__('DPH')}
          value={`${vatItems} ${currency}`}
        />
        <SummaryValue
          title={__('Cena tovaru s DPH')}
          value={`${totalItemsPrice} ${currency}`}
        />
        {showGiftcardItem && prop(giftcards[0], 'price', 0) > 0 && (
          <SummaryValue
            title={`${__('Zľavový kupón')} ${currentGiftCard}`}
            value={`-${prop(giftcards[0], 'price', 0)} ${currency}`}
          />
        )}

        {isFirstStep && (
          <StyledRow>
            <GiftcardInput
              type="text"
              value={currentGiftCard}
              onChange={handleGiftCardChange}
              placeholder="Použiť zľavový kupón"
              disabled={hasActiveGiftCard}
              shrink={1}
            />
            <GiftCardButton onClick={handleGiftCard}>
              <SvgIcon
                width={16}
                height={16}
                viewBox={'0 0 16 16'}
                icon={hasActiveGiftCard ? IconType.xSmall : IconType.rightArrow}
                fill={'#FFFFFF'}
                alt={__('Giftcard')}
                title={__('Giftcard')}
                cursor={'pointer'}
              />
            </GiftCardButton>
          </StyledRow>
        )}
        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={`${totalPrice} ${currency}`}
        />
      </DesktopFields>
      <MobileFields>
        <SummaryValue
          title={__('Cena tovaru bez DPH')}
          value={`${totalPriceWithoutVat} ${currency}`}
        />
        <SummaryValue
          title={__('DPH')}
          value={`${vatTotal} ${currency}`}
        />
        <SummaryValue
          title={__('Cena tovaru s DPH')}
          value={`${totalPrice} ${currency}`}
        />

        {showGiftcardItem && prop(giftcards[0], 'price', 0) > 0 && (
          <SummaryValue
            title={`${__('Zľavový kupón')} ${currentGiftCard}`}
            value={`-${prop(giftcards[0], 'price', 0)} ${currency}`}
          />
        )}

        {isFirstStep && (
          <StyledRow>
            <GiftcardInput
              type="text"
              value={currentGiftCard}
              onChange={handleGiftCardChange}
              placeholder="Použiť zľavový kupón"
              disabled={hasActiveGiftCard}
              shrink={1}
            />
            <GiftCardButton onClick={handleGiftCard}>
              <SvgIcon
                width={16}
                height={16}
                viewBox={'0 0 16 16'}
                icon={hasActiveGiftCard ? IconType.xSmall : IconType.rightArrow}
                fill={'#FFFFFF'}
                alt={__('Giftcard')}
                title={__('Giftcard')}
                cursor={'pointer'}
              />
            </GiftCardButton>
          </StyledRow>
        )}
        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={`${totalPrice} ${currency}`}
        />
      </MobileFields>
      <>
        <ButtonsWrapper>
          <NextButton
            disabled={isFetching}
            // tslint:disable-next-line:jsx-no-lambda
            onClick={() =>
              handleStepChange
                ? isFirstStep
                  ? handleStepChange(2)
                  : handleStepChange(3)
                : {}
            }
          >
            {isFetching ? (
              <FlexRowCenter>
                <Loader className="marginLeft" small={true} />
              </FlexRowCenter>
            ) : (
              __('Pokračovať v objednávke')
            )}
          </NextButton>
          <BackButtonWrapper>
            <SvgIcon
              width={16}
              height={16}
              viewBox={'0 0 16 16'}
              icon={IconType.leftArrow}
              fill={'black'}
              cursor={'pointer'}
              marginRight={8}
            />
            <BackButton
              to={isFirstStep ? '/' : ''}
              // tslint:disable-next-line:jsx-no-lambda
              onClick={() => (handleStepChange ? handleStepChange(1) : {})}
            >
              {backButtonText}
            </BackButton>
          </BackButtonWrapper>
        </ButtonsWrapper>
      </>
    </>
  );
}

const NextButton = styled(ButtonLink)`
  width: 100%;
  max-width: ${rem(328)};
  margin-top: ${rem(32)};
  background-color: ${({ theme }) => theme.colors.secondary};

  ${({ theme }) => theme.mediab.s450`
    margin-top: 0;
  `}

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.secondaryOnHover};
    outline: none;
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: none;
  margin-bottom: 0;
  font-weight: 500;
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  text-decoration: underline;
`;

const BackButtonWrapper = styled.div`
  margin-top: ${rem(31)};
  display: inline-block;
`;

const ButtonsWrapper = styled(FlexColCenterHorizontal)``;

const DesktopFields = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(328)};

  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}
`;

const MobileFields = styled(FlexCol)`
  display: none;
  margin-bottom: ${rem(16)};
  width: 100%;

  ${({ theme }) => theme.mediab.l925`
    display: flex;
  `}
`;

const StyledRow = styled(FlexRow)`
  margin-bottom: ${rem(24)};
  margin-top: ${rem(8)};

  &:last-child {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.mediab.l925`
    justify-content: center;
  `};
`;

const GiftCardButton = styled(Button)`
  background: ${({ theme }) => theme.colors.secondary};
  transition: filter 0.2s ease;
  margin-left: ${rem(8)};
  padding: 0;
  width: ${rem(48)};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.secondaryOnHover};
    outline: none;
  }
`;

const GiftcardInput = styled(FormInput)`
  width: 100% !important;
  max-width: 100% !important;
`;

export { SecondStep };
